/* =========================================================================
   Player-name personalization (v1.1.0 scope add). Theo can greet the player by
   name. The name comes from:
     • authed users  → their account displayName (first name only), OR
     • guests        → the GTKY memory key 'player_name' (cached locally).
   A player who SKIPS the ask stores a sentinel so we never re-ask AND never
   personalize — resolvePlayerName() returns '' for both "never asked" and
   "skipped", and lines with a {name} token are then never shown (registry).

   The interpolate/sanitize/firstName helpers are PURE (no DOM, no storage, no
   api) so they unit-test directly; resolvePlayerName() is the impure adapter.
   ========================================================================= */
import { getCurrentUser, isAuthenticated } from '../api';
import { recallGtky } from './memory';

/** Stored under memory key 'player_name' when the player skips the ask. Chosen
    so it can never collide with a sanitized real name (contains underscores). */
export const NAME_SKIP_SENTINEL = '__skipped__';

/** The GTKY memory key the name is cached under. */
export const PLAYER_NAME_KEY = 'player_name';

/** Sanitize a free-text first name: trim, allow letters (incl. accented),
    spaces, hyphen and apostrophe only, collapse runs of whitespace, cap at 20.
    Returns '' when nothing usable remains. First-names-only by intent. */
export function sanitizeName(raw: string): string {
  if (!raw) return '';
  const cleaned = raw
    .normalize('NFC')
    // Drop anything that isn't a letter, space, hyphen or apostrophe.
    .replace(/[^\p{L}\s'-]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned.slice(0, 20).trim();
}

/** The first whitespace-delimited token of a (already-trimmed) name. */
export function firstName(s: string): string {
  const t = s.trim();
  if (!t) return '';
  const sp = t.indexOf(' ');
  return sp === -1 ? t : t.slice(0, sp);
}

/** True when a stored value is a real, usable name (not empty / not the skip
    sentinel). */
export function isRealName(value: string | null | undefined): boolean {
  return !!value && value !== NAME_SKIP_SENTINEL && sanitizeName(value).length > 0;
}

/** Replace every {name} token in a line with `name`. When `name` is empty the
    token collapses cleanly (callers should avoid selecting {name} lines with no
    name, but this stays graceful either way). */
export function interpolateName(text: string, name: string): string {
  if (text.indexOf('{name}') === -1) return text;
  return text.replace(/\s*\{name\}/g, name ? ' ' + name : '').replace(/^\s+/, '');
}

/** Resolve the current display name for personalization, or '' when unknown /
    skipped. Authed users prefer their account displayName (first name); guests
    use the cached 'player_name'. Impure (reads api + storage). */
export function resolvePlayerName(): string {
  if (isAuthenticated()) {
    const u = getCurrentUser();
    if (u && u.displayName) return firstName(sanitizeName(u.displayName));
  }
  const stored = recallGtky(PLAYER_NAME_KEY);
  if (isRealName(stored)) return firstName(sanitizeName(stored as string));
  return '';
}
